import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import {
  Button,
  InlineNotification,
  TextInput,
  ToastNotification,
  Modal,
  Loading,
  SkeletonPlaceholder,
  Tag,
  Search,
  Select,
  SelectItem
} from '@carbon/react';
import { Add, Close, Hotel, TrashCan, ChevronLeft, ChevronRight, ArrowRight, Location, User, Filter } from '@carbon/icons-react';
import axiosInstance from '../utils/axiosInstance';
import authService from '../services/authService';
import PageContainer from '../components/PageContainer';
import './Rooms.css';

const getInitials = (firstName, lastName) => {
  const first = firstName ? firstName.charAt(0) : '';
  const last = lastName ? lastName.charAt(0) : '';
  return (first + last).toUpperCase() || '?';
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('sr-Latn-RS', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const Rooms = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'g100';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Negovatelj (caregiver), fizioterapeut, sestra i negovateljica smeju samo da pregledaju sobe - bez dodavanja i brisanja.
  const userRole = authService.getUserRole();
  const isCaregiver = ['caregiver', 'physiotherapist', 'nurse', 'caretaker'].includes(userRole);

  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [pagination, setPagination] = useState({
    totalCount: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [pageFading, setPageFading] = useState(false);
  
  // Filter states
  const [areas, setAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedBedsNumber, setSelectedBedsNumber] = useState('');
  // Inicijalno stanje iz URL-a (npr. /rooms?onlyAvailable=true sa admin dashboard-a)
  const [onlyAvailable, setOnlyAvailable] = useState(
    () => searchParams.get('onlyAvailable') === 'true'
  );
  const [isFilterOpen, setIsFilterOpen] = useState(
    () => searchParams.get('onlyAvailable') === 'true'
  );
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isFilterExiting, setIsFilterExiting] = useState(false);
  
  // Side panel state
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    areaId: '',
    bedsNumber: 1,
    careLevel: ''
  });
  const [formError, setFormError] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, kind: 'success', title: '', subtitle: '' });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState(null);

  // Detail side panel state
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false);
  const [detailRoom, setDetailRoom] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  useEffect(() => {
    fetchAreas();
  }, []);

  useEffect(() => {
    fetchRooms();
  }, [page, selectedArea, selectedBedsNumber, searchTerm, onlyAvailable]);

  const fetchAreas = async () => {
    try {
      const response = await axiosInstance.get('/api/Room/areas');
      if (response.data.success && Array.isArray(response.data.data)) {
        setAreas(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching areas:', error);
    }
  };

  const fetchRooms = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('pageNumber', page);
      params.append('pageSize', pageSize);
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (selectedArea) {
        params.append('areaId', selectedArea);
      }
      if (selectedBedsNumber) {
        params.append('bedsNumber', selectedBedsNumber);
      }
      if (onlyAvailable) {
        params.append('onlyAvailable', 'true');
      }
      
      const response = await axiosInstance.get(`/api/Room?${params.toString()}`);
      console.log('API Response:', response);
      
      if (response.data.success && Array.isArray(response.data.data)) {
        setRooms(response.data.data);
        if (response.data.pagination) {
          setPagination({
            totalCount: response.data.pagination.totalCount || 0,
            totalPages: response.data.pagination.totalPages || 1,
            hasNextPage: response.data.pagination.hasNextPage || false,
            hasPreviousPage: response.data.pagination.hasPreviousPage || false
          });
        }
      } else {
        setError('Neispravan format podataka');
      }
    } catch (error) {
      console.error('Error fetching rooms:', error);
      setError('Greška pri učitavanju soba');
    } finally {
      setLoading(false);
    }
  };

  const openSidePanel = () => {
    setFormData({
      name: '',
      areaId: '',
      bedsNumber: 1,
      careLevel: ''
    });
    setFormError(null);
    setIsSidePanelOpen(true);
  };

  const openRoomDetail = async (room) => {
    setDetailRoom(null);
    setDetailError(null);
    setDetailLoading(true);
    setIsDetailPanelOpen(true);

    try {
      const response = await axiosInstance.get(`/api/Room/${room.id}`);
      if (response.data.success && response.data.data) {
        setDetailRoom(response.data.data);
      } else {
        setDetailError('Neispravan format podataka');
      }
    } catch (error) {
      console.error('Error fetching room detail:', error);
      setDetailError(error.response?.data?.message || 'Greška pri učitavanju sobe');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeRoomDetail = () => {
    setIsDetailPanelOpen(false);
    setDetailRoom(null);
    setDetailError(null);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.areaId || !formData.careLevel) {
      setFormError('Molimo popunite sva obavezna polja');
      return;
    }

    setFormLoading(true);
    setFormError(null);

    try {
      const payload = {
        name: formData.name,
        areaId: formData.areaId,
        bedsNumber: parseInt(formData.bedsNumber),
        careLevel: formData.careLevel
      };

      await axiosInstance.post('/api/Room', payload);
      setToast({
        show: true,
        kind: 'success',
        title: 'Uspešno!',
        subtitle: 'Nova soba je uspešno kreirana'
      });

      setIsSidePanelOpen(false);
      fetchRooms();
      
      setTimeout(() => {
        setToast(prev => ({ ...prev, show: false }));
      }, 5000);
    } catch (error) {
      console.error('Error saving room:', error);
      setFormError(error.response?.data?.message || 'Greška pri čuvanju sobe');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteClick = (room) => {
    // Sobe se mogu obrisati samo ako su prazne (nema štićenika)
    if (room.takenBeds > 0) return;
    setRoomToDelete(room);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!roomToDelete) return;

    try {
      await axiosInstance.delete(`/api/Room/${roomToDelete.id}`);
      setToast({
        show: true,
        kind: 'success',
        title: 'Uspešno!',
        subtitle: `Soba ${roomToDelete.name} je uspešno obrisana`
      });
      setIsDeleteModalOpen(false);
      fetchRooms();
      
      setTimeout(() => {
        setToast(prev => ({ ...prev, show: false }));
      }, 5000);
    } catch (error) {
      console.error('Error deleting room:', error);
      setToast({
        show: true,
        kind: 'error',
        title: 'Greška',
        subtitle: error.response?.data?.message || 'Greška pri brisanju sobe'
      });
    }
  };

  const getBedIcon = (bedsNumber) => {
    switch (bedsNumber) {
      case 1:
        return { label: 'Jednokrevetna', count: 1 };
      case 2:
        return { label: 'Dvokrevetna', count: 2 };
      case 3:
        return { label: 'Trokrevetna', count: 3 };
      default:
        return { label: `${bedsNumber} kreveta`, count: bedsNumber };
    }
  };

  const nextPage = () => {
    if (pagination.hasNextPage) {
      setPageFading(true);
      setTimeout(() => {
        setPage(prev => prev + 1);
        setPageFading(false);
      }, 300);
    }
  };

  const handleAreaChange = (e) => {
    setSelectedArea(e.target.value);
    setPage(1);
  };

  const handleBedsNumberChange = (e) => {
    setSelectedBedsNumber(e.target.value);
    setPage(1);
  };

  const handleAvailabilityChange = (e) => {
    setOnlyAvailable(e.target.value === 'available');
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleCloseMobileFilter = () => {
    setIsFilterExiting(true);
    setTimeout(() => {
      setIsMobileFilterOpen(false);
      setIsFilterExiting(false);
    }, 300);
  };

  const prevPage = () => {
    if (pagination.hasPreviousPage) {
      setPageFading(true);
      setTimeout(() => {
        setPage(prev => prev - 1);
        setPageFading(false);
      }, 300);
    }
  };

  return (
    <PageContainer>
      <div className="rooms-container">
        {/* Toolbar */}
        <div className="rooms-toolbar">
          <div className="rooms-toolbar-search">
            <Search
              size="lg"
              placeholder="Pretražite sobe..."
              value={searchTerm}
              onChange={handleSearchChange}
              onClear={() => {
                setSearchTerm('');
                setPage(1);
              }}
              labelText=""
              closeButtonLabelText="Obriši pretragu"
              style={{ flex: '1 1 250px', maxWidth: '400px' }}
            />
            <Button
              kind="ghost"
              size="lg"
              renderIcon={Filter}
              onClick={() => setIsFilterOpen(prev => !prev)}
              hasIconOnly
              className={`rooms-filter-toggle ${isFilterOpen ? 'rooms-filter-toggle-active' : ''}`}
              iconDescription="Filteri"
              aria-label="Filteri"
              aria-expanded={isFilterOpen}
            />
          </div>
          <div className={`rooms-filters ${isFilterOpen ? 'rooms-filters-open' : ''}`}>
            <Select
              id="area-filter"
              labelText=""
              value={selectedArea}
              onChange={handleAreaChange}
              size="lg"
              style={{ minWidth: '160px' }}
            >
              <SelectItem value="" text="Sve oblasti" />
              {areas.map((area) => (
                <SelectItem key={area.id} value={area.id} text={area.name} />
              ))}
            </Select>
            
            <Select
              id="beds-filter"
              labelText=""
              value={selectedBedsNumber}
              onChange={handleBedsNumberChange}
              size="lg"
              style={{ minWidth: '160px' }}
            >
              <SelectItem value="" text="Svi kreveti" />
              {[...Array(10)].map((_, i) => (
                <SelectItem key={i + 1} value={String(i + 1)} text={`${i + 1} ${i === 0 ? 'krevet' : i < 4 ? 'kreveta' : 'kreveta'}`} />
              ))}
            </Select>
            
            <Select
              id="availability-filter"
              labelText=""
              value={onlyAvailable ? 'available' : 'all'}
              onChange={handleAvailabilityChange}
              size="lg"
              style={{ minWidth: '160px' }}
            >
              <SelectItem value="all" text="Sve sobe" />
              <SelectItem value="available" text="Slobodne" />
            </Select>
            
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              borderLeft: '1px solid var(--cds-border-subtle, #e0e0e0)',
              paddingLeft: '1rem',
              marginLeft: '0.5rem'
            }}>
              <Button
                kind="ghost"
                size="md"
                renderIcon={ChevronLeft}
                onClick={prevPage}
                hasIconOnly
                disabled={!pagination.hasPreviousPage}
                iconDescription="Prethodna stranica"
              />
              <span style={{ 
                fontSize: '0.875rem', 
                opacity: 0.8,
                minWidth: '60px',
                textAlign: 'center',
                fontWeight: '500'
              }}>
                {page}/{pagination.totalPages}
              </span>
              <Button
                kind="ghost"
                size="md"
                renderIcon={ChevronRight}
                onClick={nextPage}
                hasIconOnly
                disabled={!pagination.hasNextPage}
                iconDescription="Sledeća stranica"
              />
            </div>
          </div>
          {!isCaregiver && (
            <Button
              kind="primary"
              renderIcon={Add}
              onClick={() => openSidePanel()}
              className="add-room-button"
            >
              Dodaj sobu
            </Button>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div
            className="rooms-grid rooms-grid--loading"
            style={{
              marginBottom: '2rem'
            }}
          >
            {[...Array(4)].map((_, index) => (
              <SkeletonPlaceholder
                key={index}
                style={{
                  width: '100%',
                  height: '220px',
                  borderRadius: '8px'
                }}
              />
            ))}
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div style={{ 
            position: 'fixed', 
            top: '4rem', 
            right: '1rem', 
            zIndex: 9999,
            minWidth: '320px',
            animation: 'slideInFromRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <style>
              {`
                @keyframes slideInFromRight {
                  from {
                    transform: translateX(calc(100% + 1rem));
                    opacity: 0;
                  }
                  to {
                    transform: translateX(0);
                    opacity: 1;
                  }
                }
              `}
            </style>
            <InlineNotification
              kind="error"
              title="Greška"
              subtitle={error}
              onCloseButtonClick={() => setError(null)}
              hideCloseButton={false}
              lowContrast
            />
          </div>
        )}

        {/* Grid */}
        {!loading && !error && rooms.length > 0 && (
          <div 
            className="rooms-grid"
            style={{
              opacity: pageFading ? 0 : 1,
              transition: 'opacity 0.3s ease',
              animation: 'fadeIn 0.4s ease-in'
            }}
          >
            {rooms.map((room) => {
              const isFull = room.takenBeds === room.bedsNumber;
              const freeBeds = room.bedsNumber - room.takenBeds;
              
              return (
              <div 
                key={room.id} 
                className={`room-card ${isFull ? 'room-card-full' : ''} room-card-clickable`}
                onClick={() => openRoomDetail(room)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openRoomDetail(room);
                  }
                }}
              >
                {/* Room Number */}
                <div className="room-card-header">
                  <div className="room-number">
                    <Hotel size={24} className="room-icon" />
                    <span className="room-name">{room.name}</span>
                  </div>
                  <Tag
                    type={isFull ? 'red' : 'green'}
                    size="sm"
                  >
                    {isFull ? 'Zauzeta' : `${freeBeds} slobodno`}
                  </Tag>
                </div>

                {/* Room Details */}
                <div className="room-card-content">
                  <div className="room-card-detail">
                    <span className="detail-label">Oblast:</span>
                    <span className="detail-value">{room.areaName || '-'}</span>
                  </div>

                  <div className="room-card-detail">
                    <span className="detail-label">Broj kreveta:</span>
                    <div className="bed-count">
                      <span className="detail-value">{getBedIcon(room.bedsNumber).label}</span>
                      <div className="bed-icons-small">
                        {Array.from({ length: Math.min(room.bedsNumber, 3) }).map((_, i) => (
                          <Hotel key={i} size={16} className="bed-icon-small" />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="room-card-detail">
                    <span className="detail-label">Zauzetost:</span>
                    <span className="detail-value">{room.takenBeds}/{room.bedsNumber}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="room-card-actions">
                  <span className="room-card-details-hint">
                    <ArrowRight size={14} />
                    Detalji
                  </span>
                  {!isCaregiver && (
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={TrashCan}
                      iconDescription={freeBeds === room.bedsNumber ? 'Obriši' : 'Soba se može obrisati samo ako je prazna'}
                      hasIconOnly
                      disabled={freeBeds !== room.bedsNumber}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(room);
                      }}
                      className="delete-button"
                    />
                  )}
                </div>
              </div>
            )})}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && rooms.length === 0 && (
          <div className="rooms-empty">
            <div className="empty-icon">
              <Hotel size={64} />
            </div>
            <h3 className="empty-title">Nema soba</h3>
            <p className="empty-subtitle">
              {isCaregiver ? 'Trenutno nema soba za prikaz' : 'Dodajte prvu sobu klikom na dugme iznad'}
            </p>
          </div>
        )}

        {/* No Results */}
        {!loading && !error && pagination.totalCount === 0 && (searchTerm || selectedArea || selectedBedsNumber) && (
          <div className="rooms-empty">
            <div className="empty-icon">🔍</div>
            <h3 className="empty-title">Nema rezultata</h3>
            <p className="empty-subtitle">Pokušajte sa drugom pretragom</p>
          </div>
        )}

        {/* Right Side Panel - add only, hidden for caregivers */}
        {!isCaregiver && (
        <div className={`room-side-panel ${isSidePanelOpen ? 'open' : ''}`}>
          <div className="side-panel-header">
            <h2>Dodaj sobu</h2>
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Close}
              hasIconOnly
              onClick={() => setIsSidePanelOpen(false)}
            />
          </div>
          {formError && (
            <div style={{ 
              position: 'fixed', 
              top: '4rem', 
              right: '1rem', 
              zIndex: 9999,
              minWidth: '320px',
              animation: 'slideInFromRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <style>
                {`
                  @keyframes slideInFromRight {
                    from {
                      transform: translateX(calc(100% + 1rem));
                      opacity: 0;
                    }
                    to {
                      transform: translateX(0);
                      opacity: 1;
                    }
                  }
                `}
              </style>
              <InlineNotification
                kind="error"
                title="Greška"
                subtitle={formError}
                onCloseButtonClick={() => setFormError(null)}
                hideCloseButton={false}
                lowContrast
              />
            </div>
          )}
          <div className="side-panel-content">

            <form onSubmit={handleSubmit} className="room-form">
              <TextInput
                id="name"
                labelText="Broj sobe *"
                placeholder="npr. 104"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                required
                disabled={formLoading}
              />

              <Select
                id="areaId"
                labelText="Oblast *"
                value={formData.areaId}
                onChange={(e) => handleFormChange('areaId', e.target.value)}
                required
                disabled={formLoading}
              >
                <SelectItem value="" text="Izaberite oblast" />
                {areas.map((area) => (
                  <SelectItem key={area.id} value={area.id} text={area.name} />
                ))}
              </Select>

              <Select
                id="bedsNumber"
                labelText="Broj kreveta *"
                value={String(formData.bedsNumber)}
                onChange={(e) => handleFormChange('bedsNumber', parseInt(e.target.value, 10))}
                required
                disabled={formLoading}
              >
                {[...Array(10)].map((_, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)} text={`${i + 1} ${i === 0 ? 'krevet' : i < 4 ? 'kreveta' : 'kreveta'}`} />
                ))}
              </Select>

              <TextInput
                id="careLevel"
                labelText="Nivo nege *"
                placeholder="npr. Visoka nega, I i II stepen"
                value={formData.careLevel}
                onChange={(e) => handleFormChange('careLevel', e.target.value)}
                required
                disabled={formLoading}
              />

              <div className="form-actions">
                <Button
                  kind="secondary"
                  onClick={() => setIsSidePanelOpen(false)}
                  disabled={formLoading}
                >
                  Otkaži
                </Button>
                <Button
                  kind="primary"
                  type="submit"
                  disabled={formLoading}
                >
                  {formLoading ? 'Čuvanje...' : 'Sačuvaj'}
                </Button>
              </div>
            </form>
          </div>
        </div>
        )}

        {/* Room Detail Side Panel */}
        <div className={`room-side-panel ${isDetailPanelOpen ? 'open' : ''}`}>
          <div className="side-panel-header">
            <h2>Detalji sobe</h2>
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Close}
              hasIconOnly
              onClick={closeRoomDetail}
            />
          </div>
          <div className="side-panel-content">
            {detailLoading && (
              <div className="room-detail-loading">
                <Loading description="Učitavanje sobe..." withOverlay={false} small />
              </div>
            )}

            {detailError && !detailLoading && (
              <InlineNotification
                kind="error"
                title="Greška"
                subtitle={detailError}
                onCloseButtonClick={() => setDetailError(null)}
                hideCloseButton={false}
                lowContrast
              />
            )}

            {detailRoom && !detailLoading && (
              <div className="room-detail">
                {/* Header */}
                <div className="room-detail-header">
                  <div className="room-detail-icon">
                    <Hotel size={32} />
                  </div>
                  <div className="room-detail-title">
                    <h3>Soba {detailRoom.name}</h3>
                    <span className="room-detail-area">
                      <Location size={14} />
                      {detailRoom.areaName || '-'}
                    </span>
                  </div>
                  <Tag
                    type={detailRoom.takenBeds === detailRoom.bedsNumber ? 'red' : 'green'}
                    size="sm"
                  >
                    {detailRoom.takenBeds === detailRoom.bedsNumber
                      ? 'Zauzeta'
                      : `${detailRoom.bedsNumber - detailRoom.takenBeds} slobodno`}
                  </Tag>
                </div>

                {/* Stats */}
                <div className="room-detail-stats">
                  <div className="room-detail-stat">
                    <Hotel size={20} />
                    <div>
                      <span className="room-detail-stat-label">Broj kreveta</span>
                      <span className="room-detail-stat-value">{detailRoom.bedsNumber}</span>
                    </div>
                  </div>
                  <div className="room-detail-stat">
                    <User size={20} />
                    <div>
                      <span className="room-detail-stat-label">Zauzetost</span>
                      <span className="room-detail-stat-value">
                        {detailRoom.takenBeds}/{detailRoom.bedsNumber}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Info rows */}
                <div className="room-detail-info">
                  <div className="room-detail-row">
                    <span className="detail-label">Nivo nege:</span>
                    <span className="detail-value">{detailRoom.careLevel || '-'}</span>
                  </div>
                  <div className="room-detail-row">
                    <span className="detail-label">Datum kreiranja:</span>
                    <span className="detail-value">{formatDate(detailRoom.createdAt)}</span>
                  </div>
                  <div className="room-detail-row">
                    <span className="detail-label">Poslednja izmena:</span>
                    <span className="detail-value">{formatDate(detailRoom.updatedAt)}</span>
                  </div>
                </div>

                {/* Residents */}
                <div className="room-detail-residents">
                  <div className="room-detail-section-title">
                    <h4>Štićenici ({detailRoom.residents?.length || 0})</h4>
                  </div>

                  {detailRoom.residents && detailRoom.residents.length > 0 ? (
                    <div className="room-residents-list">
                      {detailRoom.residents.map((resident) => {
                        const initials = getInitials(resident.firstName, resident.lastName);
                        const hasImage = resident.imgSrc && resident.imgSrc !== '';
                        return (
                          <div
                            key={resident.id}
                            className="room-resident-item"
                            onClick={() => navigate(`/residents/${resident.id}`, { state: { from: { label: 'Sobe', route: '/rooms' } } })}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                navigate(`/residents/${resident.id}`, { state: { from: { label: 'Sobe', route: '/rooms' } } });
                              }
                            }}
                          >
                            <div className="room-resident-avatar">
                              {hasImage ? (
                                <>
                                  <img
                                    src={resident.imgSrc}
                                    alt={resident.fullName}
                                    className="room-resident-image"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextElementSibling.style.display = 'flex';
                                    }}
                                  />
                                  <div
                                    className="room-resident-initials"
                                    style={{ display: 'none' }}
                                  >
                                    {initials || '?'}
                                  </div>
                                </>
                              ) : (
                                <div className="room-resident-initials">
                                  {initials || '?'}
                                </div>
                              )}
                            </div>
                            <div className="room-resident-info">
                              <span className="room-resident-name">{resident.fullName}</span>
                              <span className="room-resident-meta">
                                {resident.age !== undefined && resident.age !== null
                                  ? `${resident.age} god.`
                                  : ''}
                                {resident.birthDate ? ` • ${formatDate(resident.birthDate)}` : ''}
                              </span>
                            </div>
                            <ArrowRight size={16} className="room-resident-arrow" />
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="room-residents-empty">
                      <User size={24} />
                      <span>Nema štićenika u ovoj sobi</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Overlay */}
        {isSidePanelOpen && (
          <div 
            className="side-panel-overlay" 
            onClick={() => setIsSidePanelOpen(false)}
          />
        )}

        {/* Detail Panel Overlay */}
        {isDetailPanelOpen && (
          <div 
            className="side-panel-overlay" 
            onClick={closeRoomDetail}
          />
        )}

        {/* Toast Notification */}
        {toast.show && (
          <div className="toast-container">
            <ToastNotification
              kind={toast.kind}
              title={toast.title}
              subtitle={toast.subtitle}
              timeout={0}
              onClose={() => setToast(prev => ({ ...prev, show: false }))}
              lowContrast
            />
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <Modal
          open={isDeleteModalOpen}
          onRequestClose={() => setIsDeleteModalOpen(false)}
          modalHeading="Brisanje sobe"
          primaryButtonText="Obriši"
          secondaryButtonText="Otkaži"
          onRequestSubmit={confirmDelete}
          danger
          size="sm"
        >
          <p>
            Da li ste sigurni da želite da obrišete sobu{' '}
            <strong>{roomToDelete?.name}</strong>?
          </p>
          <p style={{ marginTop: '1rem', fontSize: '0.875rem', opacity: 0.7 }}>
            Ova akcija se ne može poništiti.
          </p>
        </Modal>

        {/* Mobile Filter Button */}
        {ReactDOM.createPortal(
          <button
            className="floating-button room-mobile-filter-btn"
            onClick={() => setIsMobileFilterOpen(true)}
            style={{
              position: 'fixed',
              bottom: '2rem',
              left: '2rem',
              height: '56px',
              padding: '0 24px',
              backgroundColor: '#9fb746',
              border: 'none',
              borderRadius: '28px',
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              gap: '12px',
              outline: 'none',
              zIndex: 9998
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#8da63d';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#9fb746';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(0.97)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(1)';
            }}
          >
            <Filter
              size={24}
              style={{
                flexShrink: 0,
                minWidth: '24px',
                minHeight: '24px',
                fill: '#ffffff'
              }}
              aria-label="Filteri"
              color="#ffffff"
            />
            <span style={{
              color: '#ffffff',
              fontWeight: '500',
              fontSize: '0.95rem',
              whiteSpace: 'nowrap',
              userSelect: 'none'
            }}>
              Filteri
            </span>
          </button>,
          document.body
        )}

        {/* Floating Action Button - Dodaj sobu (mobile only) */}
        {!isCaregiver && !isSidePanelOpen && ReactDOM.createPortal(
          <button
            className="floating-button room-fab"
            onClick={() => openSidePanel()}
            style={{
              position: 'fixed',
              bottom: '2rem',
              right: '2rem',
              height: '56px',
              padding: '0 24px',
              backgroundColor: '#9fb746',
              border: 'none',
              borderRadius: '28px',
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: 'none',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              gap: '12px',
              outline: 'none',
              zIndex: 9999
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#8da63d';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#9fb746';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(0.97)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(1)';
            }}
          >
            <Add
              size={24}
              style={{
                flexShrink: 0,
                minWidth: '24px',
                minHeight: '24px',
                fill: '#ffffff'
              }}
              aria-label="Dodaj sobu"
              color="#ffffff"
            />
            <span style={{
              color: 'white',
              fontWeight: '500',
              fontSize: '0.95rem',
              whiteSpace: 'nowrap',
              userSelect: 'none'
            }}>
              Dodaj sobu
            </span>
          </button>,
          document.body
        )}

        {/* Mobile Filter Modal */}
        {isMobileFilterOpen && ReactDOM.createPortal(
          <div
            className={isFilterExiting ? 'filter-modal-exiting' : ''}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: isDarkMode ? 'rgba(22, 22, 22, 0.85)' : 'rgba(244, 244, 244, 0.85)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'flex-end',
              animation: isFilterExiting ? 'fadeOut 0.2s ease-out' : 'fadeIn 0.2s ease-out'
            }}
            onClick={handleCloseMobileFilter}
          >
            <div
              className="room-mobile-filter-panel"
              data-carbon-theme={theme}
              style={{
                width: '100%',
                backgroundColor: isDarkMode ? '#262626' : '#ffffff',
                borderRadius: '16px 16px 0 0',
                padding: '1.5rem',
                animation: isFilterExiting ? 'slideDownOut 0.3s ease-out' : 'slideUpIn 0.3s ease-out',
                maxHeight: '70vh',
                overflowY: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '500', color: isDarkMode ? '#ffffff' : '#161616' }}>Filteri</h3>
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  className="room-mobile-filter-close-btn"
                  renderIcon={Close}
                  iconDescription="Zatvori"
                  onClick={handleCloseMobileFilter}
                  style={{ color: isDarkMode ? '#ffffff' : '#161616' }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <Select
                  id="mobile-area-filter"
                  labelText="Oblast"
                  value={selectedArea}
                  onChange={handleAreaChange}
                  size="md"
                >
                  <SelectItem value="" text="Sve oblasti" />
                  {areas.map((area) => (
                    <SelectItem key={area.id} value={area.id} text={area.name} />
                  ))}
                </Select>

                <Select
                  id="mobile-beds-filter"
                  labelText="Broj kreveta"
                  value={selectedBedsNumber}
                  onChange={handleBedsNumberChange}
                  size="md"
                >
                  <SelectItem value="" text="Svi kreveti" />
                  {[...Array(10)].map((_, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)} text={`${i + 1} ${i === 0 ? 'krevet' : i < 4 ? 'kreveta' : 'kreveta'}`} />
                  ))}
                </Select>

                <Select
                  id="mobile-availability-filter"
                  labelText="Dostupnost"
                  value={onlyAvailable ? 'available' : 'all'}
                  onChange={handleAvailabilityChange}
                  size="md"
                >
                  <SelectItem value="all" text="Sve sobe" />
                  <SelectItem value="available" text="Slobodne" />
                </Select>

                <Button
                  kind="primary"
                  size="md"
                  style={{ width: '100%', marginTop: '1rem' }}
                  onClick={handleCloseMobileFilter}
                >
                  Primeni filtere
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </PageContainer>
  );
};

export default Rooms;
